import { action, extendObservable } from 'mobx';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { apiCall } from '../API';
import { POLICY_MIGRATION_STEP_ENUM } from '../constants';
import {
    browserIsolationLicense,
    emailAndSaasIsolationLicense,
    emailIsolationLicense,
    saasIsolationLicense,
    tapAndSaaSIsolationLicense,
    tapIsolationLicense
} from '../productLicense';
import AsyncActionStore from './AsyncActionStore';
import FlagStore from './FlagStore';
import AppSwitcherStore from './AppSwitcherStore';

export const ISOLATION_CHANNELS = {
    URL_ISOLATION: 'URL_ISOLATION',
    SAAS_ISOLATION: 'SAAS_ISOLATION',
    BROWSING_DEFENSE: 'BROWSING_DEFENSE'
};

export const POLICY_MENU_DISPLAYS = {
    OLD_ONLY: 'OLD_ONLY',
    NEW_ONLY: 'NEW_ONLY',
    OLD_AND_NEW: 'OLD_AND_NEW'
};

export default class ConsoleStore {
    static PropType = PropTypes.shape({
        customerId: PropTypes.number.isRequired,
        ctsId: PropTypes.string,
        analyticsId: PropTypes.string.isRequired,
        customerName: PropTypes.string.isRequired,
        userId: PropTypes.number.isRequired,
        userEmail: PropTypes.string.isRequired,
        userName: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        authority: PropTypes.string.isRequired,
        isConsoleAdmin: PropTypes.bool.isRequired,
        isSuperAdmin: PropTypes.bool.isRequired,
        onlyWebmail: PropTypes.bool.isRequired,
        license: PropTypes.string.isRequired,
        urlIsolationProductEnabled: PropTypes.bool.isRequired,
        saasIsolationProductEnabled: PropTypes.bool.isRequired,
        stpLicenseEnabled: PropTypes.bool.isRequired,
        ssoStatus: PropTypes.string.isRequired,
        isSSOUser: PropTypes.bool.isRequired,
        newPolicies: PropTypes.bool.isRequired,
        userMenu: FlagStore.PropType.isRequired,
        launchBrowser: PropTypes.func.isRequired,
        logout: PropTypes.func.isRequired,
        loadPBUrl: PropTypes.func.isRequired,
        productBoardFrameUrl: PropTypes.string.isRequired,
        apiCategories: PropTypes.func.isRequired,
        appSwitcher: AppSwitcherStore.PropType.isRequired,
        useRedesignedPolicy: PropTypes.bool,
        showPolicyMigrationMenu: PropTypes.bool,
        canAccessBrowserIso: PropTypes.func.isRequired,
        hasTapUrlIsolationLicense: PropTypes.func.isRequired,
        hasUrlIsolation: PropTypes.func.isRequired,
        customerDomains: MobxPropTypes.arrayOrObservableArrayOf(PropTypes.string).isRequired,
        platformUrl: PropTypes.string,
        policyMigrationStep: PropTypes.string,
        policyMenuDisplay: PropTypes.string,
        publicApiV3Enabled: PropTypes.bool,
        allowConsoleApi: PropTypes.bool,
        isFTREnabled: PropTypes.func.isRequired,
        isDLPEnabled: PropTypes.func.isRequired
    });

    constructor(data = {}, useLocalStorage = false) {
        this.useLocalStorage = useLocalStorage;
        this.expirePBUrlTm = null;

        extendObservable(this, /** @class ConsoleStore */{
            customerId: 0,
            ctsId: '',
            analyticsId: '',
            customerName: '',
            userId: 0,
            userEmail: '',
            userName: '',
            firstName: '',
            lastName: '',
            authority: '',
            isConsoleAdmin: false,
            isSuperAdmin: false,
            isCustomerAdmin: false,
            onlyWebmail: false,
            license: browserIsolationLicense,
            urlIsolationProductEnabled: false,
            saasIsolationProductEnabled: false,
            stpLicenseEnabled: false,
            ssoStatus: '',
            isSSOUser: false,
            newPolicies: false,
            userMenu: new FlagStore(false),
            appSwitcher: new AppSwitcherStore({
                selectedApp: null,
                apps: [],
            }),

            get isSSOConfigured() {
                return this.ssoStatus !== 'NO_CONFIGURATION';
            },

            get isSSOEnabled() {
                return this.ssoStatus === 'ENABLED';
            },

            get isOnlySSOLicense() {
                return this.license === saasIsolationLicense;
            },

            launchBrowser: () => {
                window.open('/', '_blank');
            },
            logout: () => {
                window.location.href = '/console/logout';
            },
            apiPBUrl: apiCall('/console/product-board-url', { isUserAction: false }),
            pbUrlStatus: new AsyncActionStore(),
            productBoardFrameUrl: '',
            apiCategories: apiCall('/console/categories'),
            useRedesignedPolicy: false,
            showPolicyMigrationMenu: false,
            customerDomains: [],
            policyMigrationStep: POLICY_MIGRATION_STEP_ENUM.OLD_ONLY,
            policyMenuDisplay: this.getPolicyMenuDisplay(data),
            useFileThreatRules: false,
            publicApiV3Enabled: false,
            allowConsoleApi: false,
            platformUrl: '',
            mirrorFeatureFlags: [],
        }, data);

        this.loadPBUrl();
    }

    setPolicyMigrationStep = action((step) => {
        this.policyMigrationStep = step;
        this.useRedesignedPolicy = step !== POLICY_MIGRATION_STEP_ENUM.OLD_ONLY;
        this.policyMenuDisplay = this.getPolicyMenuDisplay(this);
    });

    setFileThreatRules = action((isEnabled) => {
        this.useFileThreatRules = isEnabled;
    });

    getPolicyMenuDisplay = ({ useRedesignedPolicy, policyMigrationStep }) => {
        if (policyMigrationStep) {
            if (policyMigrationStep === POLICY_MIGRATION_STEP_ENUM.OLD_ONLY) {
                return POLICY_MENU_DISPLAYS.OLD_ONLY;
            }
            if (policyMigrationStep === POLICY_MIGRATION_STEP_ENUM.NEW_ONLY) {
                return POLICY_MENU_DISPLAYS.NEW_ONLY;
            }
            return POLICY_MENU_DISPLAYS.OLD_AND_NEW;
        }
        return useRedesignedPolicy ? POLICY_MENU_DISPLAYS.NEW_ONLY : POLICY_MENU_DISPLAYS.OLD_ONLY;
    }

    menuHasOld = () => [POLICY_MENU_DISPLAYS.OLD_AND_NEW, POLICY_MENU_DISPLAYS.OLD_ONLY]
        .includes(this.policyMenuDisplay);

    menuHasNew = () => [POLICY_MENU_DISPLAYS.OLD_AND_NEW, POLICY_MENU_DISPLAYS.NEW_ONLY]
        .includes(this.policyMenuDisplay);

    useGeneralOptions = () => [
        POLICY_MIGRATION_STEP_ENUM.OLD_INACTIVE,
        POLICY_MIGRATION_STEP_ENUM.NEW_ONLY
    ].includes(this.policyMigrationStep)

    loadPBUrl = action(() => {
        if (this.productBoardFrameUrl || this.apiPBUrl.isLoading) {
            return;
        }
        if (!this.isCustomerAdmin) { // unauthorized user
            return;
        }
        this.pbUrlStatus.setLoading(this.apiPBUrl())
            .then(action(({ value }) => {
                this.productBoardFrameUrl = value;
                clearTimeout(this.expirePBUrlTm);
                this.expirePBUrlTm = setTimeout(action(() => {
                    this.productBoardFrameUrl = '';
                }), 60 * 60 * 1000);
            }));
    });

    canAccessBrowserIso = () => ![
        saasIsolationLicense,
        tapIsolationLicense,
        tapAndSaaSIsolationLicense,
    ].includes(this.license);

    hasBrowserOrEmailLicense = () => [
        browserIsolationLicense,
        emailIsolationLicense,
        emailAndSaasIsolationLicense,
    ].includes(this.license);

    hasTapUrlIsolationLicense = () => [
        tapIsolationLicense,
        tapAndSaaSIsolationLicense,
    ].includes(this.license);

    hasUrlIsolation = () => {
        if (this.hasTapUrlIsolationLicense()) {
            return true;
        }
        return [
            browserIsolationLicense,
            emailIsolationLicense,
            emailAndSaasIsolationLicense,
        ].includes(this.license) && this.urlIsolationProductEnabled;
    };

    hasSaasIsolation = () => [
        saasIsolationLicense,
        emailAndSaasIsolationLicense,
        tapAndSaaSIsolationLicense,
    ].includes(this.license) || this.saasIsolationProductEnabled;

    showDLPOptions = () => (this.license === browserIsolationLicense) && this.saasIsolationProductEnabled;

    setSSOStatus = action((status) => {
        this.ssoStatus = status;
    });

    setSSOStatusDisabled = () => this.setSSOStatus('DISABLED');

    filterIsolationChannelBasedOnLicense = (channelValue) => {
        switch (channelValue) {
        case ISOLATION_CHANNELS.SAAS_ISOLATION:
            return !this.hasSaasIsolation();
        case ISOLATION_CHANNELS.URL_ISOLATION:
            return !this.hasUrlIsolation();
        case ISOLATION_CHANNELS.BROWSING_DEFENSE:
            return !this.hasBrowserOrEmailLicense();
        default:
            return true;
        }
    };

    licenseHasUploadDownload = () => ![
        saasIsolationLicense,
        tapIsolationLicense,
        tapAndSaaSIsolationLicense,
    ].includes(this.license);

    isFTREnabled = () => this.useFileThreatRules &&
        (this.hasBrowserOrEmailLicense() || this.hasTapUrlIsolationLicense());

    isDLPEnabled = () => this.useFileThreatRules && this.license === browserIsolationLicense;

    isMirrorFeatureEnabled = (featureName) => this.mirrorFeatureFlags.includes(featureName);

    canCustomizeBrowserBar = () => this.hasTapUrlIsolationLicense()
        || this.canAccessBrowserIso()
        || this.isConsoleAdmin;
}
